import { useEffect } from 'react';
import { getSession, sessionStore } from '../api/session';
import { platform } from '../lib/device';
import { createStore, useStore } from '../lib/store';
import { settingsStore, type Settings } from '../settings';
import {
  DEFAULT_AUDIO_BOOST,
  EQ_BAND_Q,
  EQ_BANDS,
  MAX_EQ_BALANCE,
  boostFromPercent,
  boostToLinearGain,
  decibelsToGain,
  equalizerEquals,
  isEqualizerFlat,
  normalizeEqualizer,
  type EqualizerSettings,
} from './audioFxMath';
import { player } from './engine';

/**
 * Audio boost + equalizer on top of the engine's <audio> element, driven by
 * `settings.audioBoost` (percent) and `settings.equalizer`. The UI changes
 * those with the settings helpers (`setAudioBoost`, `setEqualizer`,
 * `resetEqualizer`); this module follows them.
 *
 * - Boost at or below 100 % only attenuates: the element volume does that,
 *   no Web Audio needed.
 * - Boost above 100 % and any non-flat equalizer need a Web Audio graph
 *   (MediaElementSource → preamp → 5 biquads → panner → boost gain →
 *   limiter). It is attached lazily, from a user gesture, the first time such
 *   a setting is active, and stays attached for the element's lifetime (the
 *   browser does not allow detaching it).
 * - The graph is refused where it would break playback: no Web Audio, iOS
 *   (processed audio stops on the lock screen), or a server on another origin
 *   (media without CORS would be routed through the graph as silence).
 */

export type AudioFxStatus =
  /** Settings are at their defaults (or only attenuating); nothing to process. */
  | 'off'
  /** Processing is needed and allowed; waiting for a tap to start Web Audio. */
  | 'pending'
  | 'active'
  | 'unsupported'
  | 'ios'
  | 'cross-origin'
  | 'error';

export interface AudioFxState {
  /** Flutter's 0..2 level (1 = 100 %). */
  boost: number;
  eq: EqualizerSettings;
  status: AudioFxStatus;
  /** Plain-language explanation when effects can't be heard, else null. */
  message: string | null;
}

const MESSAGES: Partial<Record<AudioFxStatus, string>> = {
  pending: 'Tap play or anywhere on the screen to turn on audio effects.',
  unsupported: "This browser can't process audio, so boost above 100% and the equalizer are unavailable.",
  ios: 'On iPhone and iPad, processed audio stops when the screen locks, so boost above 100% and the equalizer are turned off.',
  'cross-origin':
    'Your server is on a different address than Plume, so the browser blocks audio processing. Boost above 100% and the equalizer need Plume to reach your server through the same address.',
};

function readSettings(s: Settings = settingsStore.get()): { boost: number; eq: EqualizerSettings } {
  return { boost: boostFromPercent(s.audioBoost), eq: normalizeEqualizer(s.equalizer) };
}

export const audioFxStore = createStore<AudioFxState>({ ...readSettings(), status: 'off', message: null });

interface Graph {
  ctx: AudioContext;
  element: HTMLAudioElement;
  preamp: GainNode;
  bands: BiquadFilterNode[];
  panner: StereoPannerNode | null;
  boost: GainNode;
}

let ctx: AudioContext | null = null;
let graph: Graph | null = null;
let initialised = false;
let gestureArmed = false;
let lastError: string | null = null;

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const g = globalThis as typeof globalThis & { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

/** True inside (or shortly after) a tap/keypress; assumed true where the API is missing. */
function hasTransientActivation(): boolean {
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return activation ? activation.isActive : true;
}

function serverIsSameOrigin(): boolean {
  const session = getSession();
  if (!session || typeof location === 'undefined') return false;
  try {
    return new URL(session.serverUrl, location.href).origin === location.origin;
  } catch {
    return false;
  }
}

/** Why the Web Audio graph can't be used right now, or null when it can. */
export function audioFxBlocker(): Extract<AudioFxStatus, 'unsupported' | 'ios' | 'cross-origin'> | null {
  if (!audioContextCtor()) return 'unsupported';
  if (platform.isIOS) return 'ios';
  if (!serverIsSameOrigin()) return 'cross-origin';
  return null;
}

function needsGraph(s: Pick<AudioFxState, 'boost' | 'eq'>): boolean {
  return s.boost > DEFAULT_AUDIO_BOOST + 0.001 || !isEqualizerFlat(s.eq);
}

function setStatus(status: AudioFxStatus) {
  const message = status === 'error' ? lastError : (MESSAGES[status] ?? null);
  audioFxStore.set((s) => (s.status === status && s.message === message ? s : { ...s, status, message }));
}

function updateGraph() {
  if (!graph) return;
  const { boost, eq } = audioFxStore.get();
  const t = graph.ctx.currentTime;
  const set = (param: AudioParam, value: number) => {
    try {
      param.setTargetAtTime(value, t, 0.015);
    } catch {
      param.value = value;
    }
  };
  set(graph.preamp.gain, decibelsToGain(eq.preamp));
  graph.bands.forEach((band, i) => set(band.gain, eq.bands[i] ?? 0));
  if (graph.panner) set(graph.panner.pan, eq.balance / MAX_EQ_BALANCE);
  set(graph.boost.gain, boostToLinearGain(boost));
}

function buildGraph(context: AudioContext): Graph {
  const element = player.getAudioElement();
  const source = context.createMediaElementSource(element);
  const preamp = context.createGain();
  const bands = EQ_BANDS.map((spec) => {
    const f = context.createBiquadFilter();
    f.type = spec.type;
    f.frequency.value = spec.frequency;
    if (spec.type === 'peaking') f.Q.value = EQ_BAND_Q;
    return f;
  });
  const panner = typeof context.createStereoPanner === 'function' ? context.createStereoPanner() : null;
  const boost = context.createGain();
  // Limiter so +12 dB doesn't clip (Android's LoudnessEnhancer limits too).
  const limiter = context.createDynamicsCompressor();
  limiter.threshold.value = -1;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.25;

  const chain: AudioNode[] = [source, preamp, ...bands, ...(panner ? [panner] : []), boost, limiter, context.destination];
  for (let i = 0; i < chain.length - 1; i++) chain[i]!.connect(chain[i + 1]!);
  return { ctx: context, element, preamp, bands, panner, boost };
}

/** Starts (or resumes) Web Audio and attaches the graph once the context runs. */
function startGraph() {
  const Ctor = audioContextCtor();
  if (!Ctor) return;
  try {
    ctx ??= new Ctor({ latencyHint: 'playback' });
    ctx.onstatechange = () => apply();
    if (ctx.state !== 'running') void ctx.resume().then(() => apply(), () => undefined);
    // A suspended context would silence the element, so attach only while running.
    if (ctx.state === 'running' && !graph) {
      graph = buildGraph(ctx);
      player.setOutputVolume(1);
    }
    lastError = null;
  } catch (err) {
    lastError = (err as Error)?.message || 'Audio effects could not start.';
    console.warn('[audioFx] could not start Web Audio', err);
  }
}

function onGesture() {
  disarmGesture();
  startGraph();
  apply();
}

function armGesture() {
  if (gestureArmed || typeof document === 'undefined') return;
  gestureArmed = true;
  document.addEventListener('pointerdown', onGesture, { capture: true });
  document.addEventListener('keydown', onGesture, { capture: true });
}

function disarmGesture() {
  if (!gestureArmed) return;
  gestureArmed = false;
  document.removeEventListener('pointerdown', onGesture, { capture: true });
  document.removeEventListener('keydown', onGesture, { capture: true });
}

/** Brings the audio in line with the current settings. */
function apply(inGesture = false) {
  const state = audioFxStore.get();
  if (graph) {
    updateGraph();
    player.setOutputVolume(1);
    if (graph.ctx.state !== 'running') {
      // Resume at the next tap or play.
      armGesture();
      setStatus('pending');
    } else {
      disarmGesture();
      setStatus(!getSession() || serverIsSameOrigin() ? 'active' : 'cross-origin');
    }
    return;
  }

  player.setOutputVolume(Math.min(state.boost, 1));
  if (!needsGraph(state)) {
    disarmGesture();
    setStatus('off');
    return;
  }
  const blocker = audioFxBlocker();
  if (blocker) {
    disarmGesture();
    setStatus(blocker);
    return;
  }
  // A context that is already running (resumed earlier) needs no gesture.
  if (inGesture || ctx?.state === 'running') startGraph();
  if (graph) {
    apply();
    return;
  }
  if (lastError) {
    setStatus('error');
    return;
  }
  armGesture();
  setStatus('pending');
}

/**
 * Wires audio effects to settings, the session and the player. Idempotent.
 * Call once at app start so a saved boost/EQ applies before the player opens.
 */
export function initAudioFx(): void {
  if (initialised) return;
  initialised = true;
  settingsStore.subscribe(() => {
    const next = readSettings();
    const cur = audioFxStore.get();
    if (Math.abs(next.boost - cur.boost) < 1e-9 && equalizerEquals(next.eq, cur.eq)) return;
    audioFxStore.set((s) => ({ ...s, ...next }));
    // Slider handlers update settings synchronously, so this usually runs inside the gesture.
    apply(hasTransientActivation());
  });
  sessionStore.subscribe(() => {
    // Switching to a server on another origin after the graph is attached:
    // media must now be fetched in CORS mode or it plays as silence.
    if (graph && !serverIsSameOrigin()) graph.element.crossOrigin = 'anonymous';
    apply();
  });
  player.events.on('play', () => {
    if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => undefined);
  });
  apply();
}

/** Status and effective values for the Audio Boost / Equalizer sheets. */
export function useAudioFx(): AudioFxState {
  useEffect(() => initAudioFx(), []);
  return useStore(audioFxStore);
}
